import React, { Suspense } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/analytics";

import {
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  Grid,
  Link,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { MobileView } from "react-device-detect";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import GroupIcon from "@mui/icons-material/Group";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";

import logoImage from "../../assets/Logo.png";
import logoIcon from "../../assets/Round-Logo-Design.png";
import AppStore from "../../assets/ios/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg";
import NewColors from "../../themes/NewColors";

const StandardButton = React.lazy(
  () => import("../../components/StandardButton"),
);

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    const subscribe = firebase.auth().onAuthStateChanged((user) => {
      if (user) {
        navigate("/home");
        return;
      }
      navigate("/");
      setLoading(false);
    });

    return () => subscribe();
  }, []);

  React.useEffect(() => {
    firebase.analytics().logEvent("page_view", {
      page_title: "Landing Page",
      page_location: "/",
    });
  }, []);

  const FeatureCard = ({ icon, title, description }) => (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: 3,
        transition: "transform 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 6,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, textAlign: "center", p: 3 }}>
        <Box sx={{ mb: 2, color: NewColors.triadicPower.primary }}>{icon}</Box>
        <Typography
          variant="h6"
          component="h3"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );

  const ActionCard = ({
    icon,
    title,
    description,
    buttonText,
    buttonColor,
    onClick,
    secondary = false,
  }) => (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: secondary ? 2 : 4,
        border: secondary ? `1px solid ${NewColors.active.primary}20` : "none",
        transition: "transform 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: secondary ? 4 : 6,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, textAlign: "center", p: 4 }}>
        <Box
          sx={{
            mb: 2,
            color: secondary
              ? NewColors.active.primary
              : NewColors.status.error,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h5"
          component="h3"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {description}
        </Typography>
        <StandardButton
          variant={buttonColor === "blue" ? "primary" : "error"}
          onClick={onClick}
          sx={{ width: "100%", maxWidth: 280, py: 1.5 }}
        >
          <Typography sx={{ fontSize: isMobile ? 16 : 18, fontWeight: 600 }}>
            {buttonText}
          </Typography>
        </StandardButton>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Suspense fallback={<Box />}>
        <Box
          sx={{
            minHeight: "100vh",
            background: `linear-gradient(135deg, ${NewColors.pageBackground} 0%, ${NewColors.sectionBackground} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <img
              alt="Mighty45 Logo"
              width={isMobile ? "120px" : "146px"}
              height={isMobile ? "64px" : "78px"}
              src={logoImage}
            />
            <Typography
              variant="h6"
              sx={{ mt: 2, color: NewColors.active.primary }}
            >
              Loading...
            </Typography>
          </Box>
        </Box>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<Box />}>
      <Box
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(135deg, ${NewColors.pageBackground} 0%, ${NewColors.sectionBackground} 100%)`,
          position: "relative",
          pb: 8,
        }}
      >
        {/* Header */}
        <AppBar
          elevation={0}
          position="sticky"
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            borderBottom: `1px solid ${NewColors.active.primary}20`,
          }}
        >
          <Toolbar sx={{ py: 1 }}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <img
                  alt="Mighty45 Logo"
                  width={isMobile ? "120px" : "146px"}
                  height={isMobile ? "64px" : "78px"}
                  src={logoImage}
                />
              </Box>
              {!isMobile && (
                <StandardButton
                  variant="primary"
                  onClick={() => navigate("/signin")}
                  sx={{ px: 4, py: 1.5 }}
                >
                  <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                    Sign In
                  </Typography>
                </StandardButton>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Hero Section */}
          <Box
            sx={{
              textAlign: "center",
              pt: { xs: 4, md: 6 },
              pb: { xs: 4, md: 6 },
            }}
          >
            <Box sx={{ mb: 4 }}>
              <img
                width={isMobile ? "80%" : "20%"}
                src={logoIcon}
                alt="Mighty45 Logo"
                style={{ maxWidth: 200 }}
              />
            </Box>

            <Typography
              variant={isMobile ? "h3" : "h2"}
              sx={{
                fontWeight: 700,
                mb: 3,
                background: `linear-gradient(135deg, ${NewColors.active.primary} 0%, ${NewColors.status.error} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Welcome to{" "}
              <span style={{ color: NewColors.status.error }}>MIGHTY</span>45
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                color: "text.secondary",
                mb: 4,
                maxWidth: 600,
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              The premier fitness coaching platform designed to help trainers
              build, manage, and grow their coaching business with powerful
              tools and insights.
            </Typography>
          </Box>

          {/* Features Section */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                fontWeight: 600,
                mb: 2,
                color: NewColors.active.primary,
              }}
            >
              Why Choose Mighty45?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: "text.secondary",
                mb: 6,
                maxWidth: 500,
                mx: "auto",
              }}
            >
              Everything you need to manage your coaching business and help your
              clients succeed
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <FeatureCard
                  icon={<GroupIcon sx={{ fontSize: 48 }} />}
                  title="Client Management"
                  description="Comprehensive client profiles, progress tracking, and communication tools to keep your clients engaged and motivated."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FeatureCard
                  icon={<FitnessCenterIcon sx={{ fontSize: 48 }} />}
                  title="Workout & Meal Plans"
                  description="Create customized workout routines and meal plans with our intuitive protocol builder and extensive exercise library."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FeatureCard
                  icon={<TrendingUpIcon sx={{ fontSize: 48 }} />}
                  title="Progress Analytics"
                  description="Track client progress with detailed analytics, progress photos, measurements, and compliance reporting."
                />
              </Grid>
            </Grid>
          </Box>

          {/* Getting Started Section */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                fontWeight: 600,
                mb: 2,
                color: NewColors.active.primary,
              }}
            >
              Get Started Today
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: "text.secondary",
                mb: 6,
                maxWidth: 500,
                mx: "auto",
              }}
            >
              Choose your path to start your fitness coaching journey
            </Typography>

            <Grid container spacing={4} justifyContent="center">
              {isMobile && (
                <Grid item xs={12}>
                  <ActionCard
                    icon={<PersonIcon sx={{ fontSize: 48 }} />}
                    title="Sign In"
                    description="Already have an account? Sign in to access your dashboard and continue managing your clients."
                    buttonText="Sign In"
                    buttonColor="blue"
                    onClick={() => navigate("/signin")}
                    secondary
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <ActionCard
                  icon={<BusinessIcon sx={{ fontSize: 48 }} />}
                  title="Create New Organization"
                  description="Start your coaching business with Mighty45. Set up your organization profile and begin adding clients to grow your business."
                  buttonText="Create Organization"
                  buttonColor="red"
                  onClick={() => navigate("/onboarding")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ActionCard
                  icon={<TrackChangesIcon sx={{ fontSize: 48 }} />}
                  title="Join Existing Organization"
                  description="Have a connect code? Join an existing organization and start collaborating with your team to serve clients better."
                  buttonText="Join Organization"
                  buttonColor="red"
                  onClick={() => navigate("/connect")}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Mobile App Section */}
          <MobileView>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, mb: 2, color: NewColors.active.primary }}
              >
                Get the Mobile App
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", mb: 3 }}
              >
                Download the M45 mobile app for on-the-go client management and
                check-ins.
              </Typography>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://apps.apple.com/us/app/mighty45/id1594939720"
              >
                <img width={200} src={AppStore} alt="App Store Button" />
              </a>
            </Box>
          </MobileView>
        </Container>

        {/* Footer */}
        <Box
          sx={{
            borderTop: `1px solid ${NewColors.active.primary}20`,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            py: 3,
            px: 2,
          }}
        >
          <Grid
            container
            justifyContent="center"
            spacing={4}
            alignItems="center"
          >
            <Grid item>
              <Typography variant="body2" color="text.secondary">
                © 2024 Mighty45 LLC | All rights reserved
              </Typography>
            </Grid>
            <Grid item>
              <Grid container spacing={3}>
                <Grid item>
                  <Link
                    href="https://mighty45.com/about"
                    target="_blank"
                    sx={{
                      color: NewColors.active.primary,
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    <Typography variant="body2">About</Typography>
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Suspense>
  );
};

export default LandingPage;
